import BlockUtilsMixin from '@intercom/embercom-composer/components/mixins/block-utils';
import ActionHandlers from '@intercom/embercom-composer/components/mixins/action-handlers';
import textDiff from '@intercom/embercom-composer/lib/text-diff';
import Constants from '@intercom/embercom-composer/lib/constants';
import textWithNewlines from '@intercom/embercom-composer/lib/text-with-newlines';
import translateKeydownEvent from '@intercom/embercom-composer/lib/editor/event-translators/key-down';
import keyEventMatchers from '@intercom/embercom-composer/lib/editor/event-translators/key-event-matchers';
import { intersection } from '@intercom/embercom-composer/lib/array-utils';
import { debounceTask, runDisposables } from 'ember-lifeline';
import Em from 'ember';
export default Em.Mixin.create(BlockUtilsMixin, ActionHandlers, {
  isTyping: false,
  modifierKeyDown: false,
  syncCharacters: [' ', Constants.NON_BREAKING_SPACE, '@', ':', '#'],
  keyDown(e) {
    Em.instrument('Composer.editorView.keyDown', null, function () {
      if (this.eventIsHandledExternally(e)) {
        e.preventDefault();
        return;
      }
      this.defaultKeyDownActions(e);
      let action = translateKeydownEvent(e);
      if (action.type === 'typing' && this.isTypeOver(e)) {
        action.type = 'typeover';
      }
      if (action.type && this[action.type]) {
        action = this[action.type](action);
      }
      if (action.preventDefault || this._shouldPreventDefultOnKeydown(action)) {
        e.preventDefault();
      }
    }.bind(this));
  },
  _shouldPreventDefultOnKeydown(action) {
    return this.isBlockSelected() && !(action.type === 'copyAction' || action.type === 'cutAction');
  },
  defaultKeyDownActions(e) {
    this.set('isTyping', true);
    if (keyEventMatchers.isModifierKeyOnOS(e)) {
      this.set('modifierKeyDown', true);
    }
    debounceTask(this, 'setEditorDimensions', 500);
    this.set('contentsAtLastKeySync', this.getContentsForKeyEvent());
    this._handleContextMenuSelection();
    this.setLastActiveBlockAndSelection();
  },
  keyUp(e) {
    Em.instrument('Composer.editorView.keyUp', null, function () {
      try {
        this.setTextBeforeCursor();
        this.setLastActiveBlockAndSelection();
        this.handleSyncChars(e);
        this.checkForContent();
        this.setSelectionCoordinates();
        if (!this.eventIsHandledExternally(e)) {
          if (keyEventMatchers.isArrowKey(e) || keyEventMatchers.isUnixNavigation(e) || keyEventMatchers.isModifierKeyOnOS(e) || keyEventMatchers.isWindowKey(e)) {
            this.syncCurrentBlock();
            if (!this.isCurrentBlockAnICBlock()) {
              this.clearBlockSelection();
            } else {
              this.get('uiSelection').clearDomSelection();
              this.addSelectionBorderToCurrentBlock();
            }
          }
        }
        // If this is a type-over we need to force a sync so selection updates
        // TODO - Modify this so it correctly handles dead keys
        if (this.get('composerState.selection.hasWidth')) {
          this.syncCurrentBlockAndCheckpoint();
        }
        if (keyEventMatchers.isModifierKeyOnOS(e)) {
          this.set('modifierKeyDown', false);
        }
      } catch (error) {
        this.sendError(error);
      }
    }.bind(this));
  },
  keyPress(e) {
    try {
      if (this.isTypeOver(e)) {
        this.get('composerState').backspace();
      }
    } catch (error) {
      this.sendError(error);
    }
  },
  handleSyncChars() {
    let currentContents = this.getContentsForKeyEvent();
    let diff = textDiff(this.get('contentsAtLastKeySync'), currentContents);
    this.set('contentsAtLastKeySync', currentContents);
    this.fixFirefoxSpace(diff);
    let intersect = intersection(diff, this.get('syncCharacters'));
    if (this.get('isTypeAheadVisible') || intersect.length > 0 || this.isBacktick(diff)) {
      if (intersect.length > 1 || this.get('modifierKeyDown')) {
        this.syncCurrentBlock();
      } else {
        this.syncCurrentBlockAndCheckpoint();
      }
    }
  },
  isBacktick(diff) {
    return diff.length === 1 && diff[0] === '`';
  },
  getContentsForKeyEvent() {
    return textWithNewlines(this.get('$editor'));
  },
  fixFirefoxSpace(diff) {
    if (diff.length === 2 && diff.includes(' ') && diff.includes('\n')) {
      this.getCurrentBlock(true).find('br:last').replaceWith('&nbsp;');
    }
  },
  eventIsHandledExternally(e) {
    return this.get('isTypeAheadVisible') && (keyEventMatchers.isArrowUpOrDown(e) || keyEventMatchers.isEnter(e) || keyEventMatchers.isKeyCode('tab', e));
  },
  isTypeOver(e) {
    return !keyEventMatchers.isSpecialKey(e) && !keyEventMatchers.isNavigation(e) && !this.get('composerState.selection.isZeroWidth') && (this.get('composerState.currentEditableObject.isAttribute') || !this.get('uiSelection').isSelectionWithinASingleBlock(this.get('$editor')));
  },
  willDestroy() {
    this._super(...arguments);
    runDisposables(this);
  }
});